import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { getMangaBySlug, getRelatedManga } from '@/mocks/manga';

export default function MangaPage() {
  const { slug } = useParams<{ slug: string }>();
  const manga = slug ? getMangaBySlug(slug) : undefined;

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-file-unknow-line text-4xl text-foreground-300"></i>
            </span>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs"></i>
              </span>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedManga = getRelatedManga(slug);

  return (
    <>
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${manga.title} - 免费在线漫画阅读`,
            description: manga.description.slice(0, 160),
            author: { '@type': 'Person', name: manga.author },
            datePublished: '2024-01-01',
            dateModified: manga.updatedAt,
            publisher: {
              '@type': 'Organization',
              name: '酷漫屋',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://kumanwu.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: manga.category,
                item: `https://kumanwu.com/category/${manga.category === '日漫' ? 'japanese' : manga.category === '韩漫' ? 'korean' : 'chinese'}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: manga.title,
                item: `https://kumanwu.com/manga/${manga.slug}`,
              },
            ],
          }),
        }}
      />

      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: manga.category, href: `/category/${manga.category === '日漫' ? 'japanese' : manga.category === '韩漫' ? 'korean' : 'chinese'}` },
            { label: manga.title },
          ]}
        />

        {/* Manga Detail */}
        <article className="px-4 md:px-6 pb-10">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-full sm:w-[240px] mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={manga.coverImage}
                  alt={`${manga.title}漫画封面`}
                  title={`${manga.title} - 免费在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`px-3 py-1 text-xs rounded-full ${
                  manga.status === '连载中'
                    ? 'bg-accent-100 text-accent-800'
                    : 'bg-secondary-200 text-secondary-800'
                }`}>
                  {manga.status}
                </span>
                <span className="text-sm text-foreground-600">
                  作者：<strong>{manga.author}</strong>
                </span>
                <span className="text-sm text-foreground-600">
                  分类：<strong>{manga.category}</strong>
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-xs text-primary-400"></i>
                  </span>
                  <strong>{manga.rating}</strong> 分
                </span>
                <span className="text-sm text-foreground-500">
                  {(manga.views / 10000).toFixed(0)}万 次阅读
                </span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full border border-background-200/70 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>
              </div>

              {/* Read Button */}
              <Link
                to={`/manga/${manga.slug}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                开始阅读
              </Link>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mt-10" aria-label="章节列表">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {manga.chapters.length} 话
              </span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {manga.chapters.slice().reverse().slice(0, 48).map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/manga/${manga.slug}`}
                  className="px-3 py-2 text-sm text-foreground-700 bg-background-100 rounded-lg hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200 truncate text-center whitespace-nowrap"
                >
                  {chapter.title}
                </Link>
              ))}
            </div>
            {manga.chapters.length > 48 && (
              <p className="text-center text-sm text-foreground-500 mt-4">
                仅展示最近 48 话，更多章节请开始阅读
              </p>
            )}
          </section>

          {/* Related Manga */}
          {relatedManga.length > 0 && (
            <section className="mt-12" aria-label="相关推荐" data-product-shop>
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedManga.map((m) => (
                  <MangaCard
                    key={m.id}
                    id={m.id}
                    slug={m.slug}
                    title={m.title}
                    coverImage={m.coverImage}
                    status={m.status}
                    category={m.category}
                    rating={m.rating}
                    updatedAt={m.updatedAt}
                    chapterCount={m.chapters.length}
                  />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}